import React, { useMemo } from 'react';
import { Alert, Collapse, Empty, Space, Tag, Tooltip, Typography, theme } from 'antd';
import {
  DatabaseOutlined,
  EyeOutlined,
  FileImageOutlined,
  LockOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import {
  MOTIVO_REVISION_FALLBACK,
  formatearValorCampo,
  humanizarCampo,
  rolLabel,
  tablaLabel,
} from './solicitudesDatosLabels';

const { Text, Paragraph } = Typography;
const { useToken } = theme;

// Vista del informe del localizador: dónde aparece la persona dentro del
// sistema. Trae datos personales reales, así que se presenta como fichas
// legibles y nunca como JSON volcado en pantalla.

function CamposAparicion({ campos }) {
  const { token } = useToken();
  const entradas = Object.entries(campos ?? {});

  if (!entradas.length) {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        Sin datos identificatorios en este registro.
      </Text>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: token.marginXS,
      }}
    >
      {entradas.map(([clave, valor]) => (
        <div key={clave} style={{ minWidth: 0 }}>
          <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
            {humanizarCampo(clave)}
          </Text>
          <Text style={{ fontSize: 13, wordBreak: 'break-word' }}>
            {formatearValorCampo(valor)}
          </Text>
        </div>
      ))}
    </div>
  );
}

function AparicionCard({ aparicion }) {
  const { token } = useToken();
  // Los opcionales llegan ausentes (JSON.stringify omite undefined), no en null.
  const requiereRevision = aparicion?.requiereRevisionHumana === true;
  const obligacion = aparicion?.obligacionLegal;
  const archivos = aparicion?.s3Keys?.length ?? 0;
  const firmasSinConfirmar = aparicion?.firmasSinConfirmar?.length ?? 0;

  return (
    <div
      style={{
        border: `1px solid ${requiereRevision ? token.colorWarningBorder : token.colorBorder}`,
        borderRadius: token.borderRadius,
        background: token.colorBgContainer,
        padding: token.paddingSM,
        display: 'flex',
        flexDirection: 'column',
        gap: token.marginXS,
      }}
    >
      <Space size={6} wrap>
        <Tag color="default" style={{ margin: 0 }}>
          {rolLabel(aparicion?.rol)}
        </Tag>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Registro N.° {aparicion?.registroId ?? '—'}
        </Text>
        {requiereRevision && (
          <Tag icon={<EyeOutlined />} color="warning" style={{ margin: 0 }}>
            Necesita revisión
          </Tag>
        )}
        {obligacion != null && (
          <Tooltip title={obligacion}>
            <Tag icon={<LockOutlined />} color="blue" style={{ margin: 0 }}>
              Se conserva por ley
            </Tag>
          </Tooltip>
        )}
        {archivos > 0 && (
          <Tag icon={<FileImageOutlined />} color="default" style={{ margin: 0 }}>
            {archivos} {archivos === 1 ? 'archivo' : 'archivos'}
          </Tag>
        )}
      </Space>

      <CamposAparicion campos={aparicion?.campos} />

      {requiereRevision && (
        <Alert
          type="warning"
          showIcon
          style={{ fontSize: 12 }}
          message={aparicion?.motivoRevision ?? MOTIVO_REVISION_FALLBACK}
        />
      )}

      {obligacion != null && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {obligacion}
        </Text>
      )}

      {firmasSinConfirmar > 0 && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {firmasSinConfirmar === 1
            ? 'Hay 1 firma que no se borrará automáticamente: el registro coincide solo por nombre y no podemos confirmar que sea la misma persona.'
            : `Hay ${firmasSinConfirmar} firmas que no se borrarán automáticamente: el registro coincide solo por nombre y no podemos confirmar que sea la misma persona.`}
        </Text>
      )}
    </div>
  );
}

function InformeTitular({ informe }) {
  const { token } = useToken();

  const grupos = useMemo(() => {
    const mapa = new Map();
    (informe?.apariciones ?? []).forEach((aparicion) => {
      const clave = aparicion?.tabla ?? '__sin_origen';
      if (!mapa.has(clave)) mapa.set(clave, []);
      mapa.get(clave).push(aparicion);
    });
    // Los orígenes con más apariciones primero: es donde hay más que decidir.
    return [...mapa.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [informe]);

  if (!informe) return null;

  const totalApariciones = informe.apariciones?.length ?? 0;
  const totalRevision = informe.revisionHumana?.length ?? 0;
  const totalArchivos = informe.s3Keys?.length ?? 0;

  if (!totalApariciones) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No encontramos datos de esta persona en el sistema."
      />
    );
  }

  const identidad = informe.identidad ?? {};
  const identificadores = [identidad.nombre, identidad.rut, identidad.email].filter(Boolean);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: token.marginSM }}>
      <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 13 }}>
        Buscamos a la persona en todo el sistema y encontramos{' '}
        <Text strong>
          {totalApariciones} {totalApariciones === 1 ? 'registro' : 'registros'}
        </Text>{' '}
        {identificadores.length > 0 && <>a partir de {identificadores.join(' · ')}</>}
        {totalArchivos > 0 && (
          <>
            . Hay {totalArchivos} {totalArchivos === 1 ? 'archivo asociado' : 'archivos asociados'}{' '}
            (firmas y fotos) que se borrarían al anonimizar
          </>
        )}
        .
      </Paragraph>

      {totalRevision > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<SafetyOutlined />}
          message={
            totalRevision === 1
              ? 'Hay 1 registro que debe revisar una persona'
              : `Hay ${totalRevision} registros que debe revisar una persona`
          }
          description="No se pueden anonimizar de forma automática: el dato está dentro de un texto escrito a mano, o la persona coincide solo por su nombre y podría ser otra. Revíselos antes de anonimizar."
        />
      )}

      <Collapse
        size="small"
        defaultActiveKey={grupos.length === 1 ? [grupos[0][0]] : []}
        items={grupos.map(([tabla, apariciones]) => ({
          key: tabla,
          label: (
            <Space size={8}>
              <DatabaseOutlined style={{ color: token.colorTextTertiary }} />
              <Text strong style={{ fontSize: 13 }}>
                {tablaLabel(tabla === '__sin_origen' ? undefined : tabla)}
              </Text>
              <Tag style={{ margin: 0 }}>{apariciones.length}</Tag>
              {apariciones.some((a) => a?.requiereRevisionHumana === true) && (
                <Tag color="warning" style={{ margin: 0 }}>
                  Con revisión pendiente
                </Tag>
              )}
            </Space>
          ),
          children: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: token.marginXS }}>
              {apariciones.map((aparicion, idx) => (
                <AparicionCard
                  key={`${tabla}-${aparicion?.registroId ?? idx}`}
                  aparicion={aparicion}
                />
              ))}
            </div>
          ),
        }))}
      />
    </div>
  );
}

export default InformeTitular;
